// ============================================================
// src/lib/strava.js
// Strava OAuth 2.0 + API v3 helper
// Rollaco Cycling Academy — Tahap 4 (token di Supabase)
// ============================================================

import { supabase } from './supabase'

const CLIENT_ID     = process.env.REACT_APP_STRAVA_CLIENT_ID
const CLIENT_SECRET = process.env.REACT_APP_STRAVA_CLIENT_SECRET
const REDIRECT_URI  = process.env.REACT_APP_STRAVA_REDIRECT_URI
  || 'http://localhost:3000/strava/callback'

const SCOPE = 'activity:read_all'

// ── 1. Redirect user ke halaman login Strava ─────────────────
export function stravaLogin() {
  const params = new URLSearchParams({
    client_id:       CLIENT_ID,
    redirect_uri:    REDIRECT_URI,
    response_type:   'code',
    approval_prompt: 'auto',
    scope:           SCOPE,
  })
  window.location.href = `https://www.strava.com/oauth/authorize?${params}`
}

// ── 2. Tukar authorization code → access token ───────────────
export async function stravaExchangeToken(code) {
  const res = await fetch('https://www.strava.com/oauth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id:     CLIENT_ID,
      client_secret: CLIENT_SECRET,
      code,
      grant_type:    'authorization_code',
    }),
  })
  if (!res.ok) {
    const err = await res.json()
    throw new Error(err.message || 'Gagal tukar token Strava')
  }
  return res.json()
  // Returns: { access_token, refresh_token, expires_at, athlete }
}

// ── 3. Refresh token kalau sudah expired ─────────────────────
export async function stravaRefreshToken(refreshToken) {
  const res = await fetch('https://www.strava.com/oauth/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      client_id:     CLIENT_ID,
      client_secret: CLIENT_SECRET,
      refresh_token: refreshToken,
      grant_type:    'refresh_token',
    }),
  })
  if (!res.ok) throw new Error('Gagal refresh token Strava')
  return res.json()
}

// ── 4. Simpan token ke Supabase (tabel athletes) ─────────────
// profileId = profile.id dari Supabase Auth
export async function saveStravaTokens(profileId, { access_token, refresh_token, expires_at, athlete }) {
  const { error } = await supabase
    .from('athletes')
    .update({
      strava_athlete_id:       athlete?.id || null,
      strava_access_token:     access_token,
      strava_refresh_token:    refresh_token,
      strava_token_expires_at: expires_at,
    })
    .eq('profile_id', profileId)

  if (error) throw new Error('Gagal simpan token Strava: ' + error.message)
}

// ── 5. Ambil token dari Supabase, auto-refresh jika perlu ────
export async function getValidStravaToken(profileId) {
  const { data, error } = await supabase
    .from('athletes')
    .select('strava_access_token, strava_refresh_token, strava_token_expires_at')
    .eq('profile_id', profileId)
    .single()

  if (error || !data?.strava_access_token) return null

  const now = Math.floor(Date.now() / 1000)

  // Token masih valid (buffer 5 menit)
  if (now < data.strava_token_expires_at - 300) {
    return data.strava_access_token
  }

  // Token expired — refresh dulu
  try {
    const fresh = await stravaRefreshToken(data.strava_refresh_token)
    await saveStravaTokens(profileId, fresh)
    return fresh.access_token
  } catch (e) {
    // Refresh gagal — token tidak valid, perlu connect ulang
    await stravaDisconnect(profileId)
    return null
  }
}

// ── 6. Cek apakah user sudah connect Strava ──────────────────
export async function isStravaConnected(profileId) {
  const { data } = await supabase
    .from('athletes')
    .select('strava_access_token')
    .eq('profile_id', profileId)
    .single()

  return !!data?.strava_access_token
}

// ── 7. Disconnect Strava — hapus token dari DB ───────────────
export async function stravaDisconnect(profileId) {
  await supabase
    .from('athletes')
    .update({
      strava_athlete_id:       null,
      strava_access_token:     null,
      strava_refresh_token:    null,
      strava_token_expires_at: null,
    })
    .eq('profile_id', profileId)
}

// ── 8. Ambil aktivitas terbaru dari Strava ───────────────────
export async function stravaGetActivities(accessToken, perPage = 15) {
  const res = await fetch(
    `https://www.strava.com/api/v3/athlete/activities?per_page=${perPage}`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  )
  if (!res.ok) throw new Error('Gagal ambil aktivitas Strava')
  return res.json()
}

// ── 9. Ambil detail satu aktivitas ───────────────────────────
export async function stravaGetActivity(accessToken, activityId) {
  const res = await fetch(
    `https://www.strava.com/api/v3/activities/${activityId}`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  )
  if (!res.ok) throw new Error('Gagal ambil detail aktivitas')
  return res.json()
}

// ── 10. Mapping Strava activity → session_logs format ────────
export function mapStravaToSessionLog(activity) {
  return {
    avg_speed:          activity.average_speed
                          ? parseFloat((activity.average_speed * 3.6).toFixed(1))
                          : null,
    distance:           activity.distance
                          ? parseFloat((activity.distance / 1000).toFixed(2))
                          : null,
    duration:           activity.moving_time
                          ? Math.round(activity.moving_time / 60)
                          : null,
    cadence:            activity.average_cadence
                          ? Math.round(activity.average_cadence)
                          : null,
    heart_rate:         activity.average_heartrate
                          ? Math.round(activity.average_heartrate)
                          : null,
    power_watts:        activity.average_watts
                          ? Math.round(activity.average_watts)
                          : null,
    notes:              activity.name || null,
    strava_activity_id: activity.id?.toString() || null,
    source:             'strava',
  }
}
